import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, renderList as _renderList, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "mall-orders__shell" }
const _hoisted_2 = { class: "mall-orders__head" }
const _hoisted_3 = {
  key: 0,
  class: "mall-orders__remind"
}
const _hoisted_4 = { class: "mall-orders__toast" }
const _hoisted_5 = {
  key: 0,
  class: "mall-orders__remind-box"
}
const _hoisted_6 = { class: "mall-orders__remind-actions" }
const _hoisted_7 = {
  key: 1,
  class: "mall-orders__remind-box"
}
const _hoisted_8 = { class: "mall-orders__remind-actions" }
const _hoisted_9 = {
  key: 1,
  class: "mall-orders__remind is-confirming"
}
const _hoisted_10 = { class: "mall-orders__toast" }
const _hoisted_11 = {
  key: 0,
  class: "mall-orders__remind-box"
}
const _hoisted_12 = { key: 0 }
const _hoisted_13 = { class: "mall-orders__remind-actions" }
const _hoisted_14 = {
  key: 0,
  class: "mall-orders__tabs",
  role: "tablist"
}
const _hoisted_15 = ["aria-selected", "onClick"]
const _hoisted_16 = {
  key: 1,
  class: "mall-orders__empty"
}
const _hoisted_17 = {
  key: 2,
  class: "mall-orders__empty"
}
const _hoisted_18 = {
  key: 3,
  class: "mall-orders__empty"
}
const _hoisted_19 = {
  key: 4,
  class: "mall-orders__list"
}
const _hoisted_20 = { class: "mall-orders__summary" }
const _hoisted_21 = { key: 0 }
const _hoisted_22 = {
  key: 1,
  class: "mall-orders__ship"
}
const _hoisted_23 = { key: 0 }
const _hoisted_24 = {
  key: 2,
  class: "mall-orders__tracks"
}
const _hoisted_25 = ["onClick"]
const _hoisted_26 = { class: "mall-orders__foot-right" }

import { computed, onMounted, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import MallTrackingModal from '@/components/mall/MallTrackingModal.vue'
import MallMyFilterBar from '@/components/mall/MallMyFilterBar.vue'
import { useListDateRange } from '@/composables/useListDateRange'
import { useMallMypageEmbedded } from '@/composables/useMallMypage'
import { useMallToast } from '@/composables/useMallToast'
import { formatPrice } from '@/constants/mallProducts'
import {
  MALL_ORDER_ITEM_STATUS_LABEL,
  MALL_ORDER_STATUS_LABEL,
  mallBuyerOrderStatusLabel,
  fetchMyOrders,
  fetchPaymentInfo,
  type MallOrder,
  type MallOrderItemStatus,
  type MallOrderStatus,
  type MallPaymentInfo,
} from '@/api/mallOrder'
import { useMallTracking } from '@/composables/useMallTracking'
import { copyToClipboard } from '@/utils/clipboard'
import type { DatePresetKey } from '@/utils/dateInput'

type FilterKey = 'ALL' | MallOrderStatus | 'READY' | 'SHIPPING' | 'DELIVERED' | 'CLAIM'


export default /*@__PURE__*/_defineComponent({
  __name: 'MallOrdersPage',
  setup(__props) {

const isEmbedded = useMallMypageEmbedded()
const { showSuccess, showError } = useMallToast()
const { trackTarget, openTracking, closeTracking } = useMallTracking()

function trackItemsOf(order: MallOrder) {
  return order.trackItems || []
}

function onCloseTracking() {
  closeTracking()
  void reload()
}
const { fromDate, toDate, preset, applyPreset, inRange } = useListDateRange('3m')

const orders = ref<MallOrder[]>([])
const payment = ref<MallPaymentInfo | null>(null)
const loading = ref(true)
const filterKey = ref<FilterKey>('ALL')
const keyword = ref('')

function onFromDate(value: string) {
  fromDate.value = value
  preset.value = 'all'
}

function onToDate(value: string) {
  toDate.value = value
  preset.value = 'all'
}

function onPreset(key: DatePresetKey) {
  applyPreset(key)
  void reload()
}

async function reload() {
  loading.value = true
  try {
    const [list, pay] = await Promise.all([
      fetchMyOrders({
        from: fromDate.value || undefined,
        to: toDate.value || undefined,
        keyword: keyword.value.trim() || undefined,
      }),
      fetchPaymentInfo().catch(() => null),
    ])
    orders.value = list
    payment.value = pay
  } catch {
    orders.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void reload()
})

const pendingOrders = computed(() => orders.value.filter((order) => order.status === 'PENDING'))
const unpaidPendingOrders = computed(() =>
  pendingOrders.value.filter((order) => !order.payNotifiedAt),
)
const notifiedPendingOrders = computed(() =>
  pendingOrders.value.filter((order) => Boolean(order.payNotifiedAt)),
)
const unpaidPendingCount = computed(() => unpaidPendingOrders.value.length)
const notifiedPendingCount = computed(() => notifiedPendingOrders.value.length)
const firstUnpaidPending = computed(() => unpaidPendingOrders.value[0] || null)
const firstNotifiedPending = computed(() => notifiedPendingOrders.value[0] || null)

const oldestUnpaidPendingHours = computed(() => {
  if (!unpaidPendingOrders.value.length) return 0
  const oldest = Math.min(
    ...unpaidPendingOrders.value.map((order) => new Date(order.orderedAt).getTime()),
  )
  return Math.max(0, Math.floor((Date.now() - oldest) / 3600000))
})

function pendingAgeLabel(orderedAt: string) {
  const hours = Math.max(0, Math.floor((Date.now() - new Date(orderedAt).getTime()) / 3600000))
  if (hours >= 48) return `주문 후 ${Math.floor(hours / 24)}일 경과`
  if (hours >= 1) return `주문 후 ${hours}시간 경과`
  return '방금 주문'
}

async function copyText(value: string, label: string) {
  const ok = await copyToClipboard(value)
  if (ok) {
    showSuccess(`${label}을(를) 복사했습니다.`)
    return
  }
  showError(`${label} 복사에 실패했습니다. 직접 선택해 복사해 주세요.`)
}

function matchesFilter(order: MallOrder, key: FilterKey) {
  if (key === 'ALL') return true
  if (key === 'PENDING' || key === 'CANCELED') {
    return order.status === key
  }
  if (key === 'PAID') {
    /* 결제만 되고 아직 발송준비·배송 전이거나, 입금확인 직후 */
    if (order.status !== 'PAID') return false
    const statuses = order.itemStatuses || []
    if (!statuses.length) return true
    return statuses.some((status) => status === 'PAID' || status === 'WAITING')
  }
  if (key === 'READY') {
    return (
      order.status === 'PAID' &&
      (order.itemStatuses || []).some((status) => status === 'READY')
    )
  }
  if (key === 'SHIPPING') {
    return (
      order.status === 'PAID' &&
      (order.itemStatuses || []).some((status) => status === 'SHIPPING')
    )
  }
  if (key === 'DELIVERED') {
    return (
      order.status === 'PAID' &&
      (order.itemStatuses || []).some((status) =>
        status === 'DELIVERED' || status === 'DONE',
      )
    )
  }
  if (key === 'CLAIM') {
    return (order.itemStatuses || []).some((status) =>
      ['CANCEL_REQUEST', 'RETURN_REQUEST', 'CANCELED'].includes(status),
    )
  }
  return true
}

/** 주문 카드 오른쪽 배지. 입금 확인 뒤에는 상품 진행 상태를 보여 준다. */
function buyerStatusLabel(order: MallOrder) {
  if (order.status !== 'PAID') {
    return mallBuyerOrderStatusLabel(order)
  }
  const statuses = (order.itemStatuses || []).filter((status) => status !== 'CANCELED')
  if (!statuses.length) return MALL_ORDER_STATUS_LABEL.PAID
  if (statuses.some((status) => status === 'SHIPPING')) return MALL_ORDER_ITEM_STATUS_LABEL.SHIPPING
  if (statuses.some((status) => status === 'READY')) return MALL_ORDER_ITEM_STATUS_LABEL.READY
  if (statuses.some((status) => status === 'DELIVERED')) {
    return MALL_ORDER_ITEM_STATUS_LABEL.DELIVERED
  }
  if (statuses.every((status) => status === 'DONE')) return MALL_ORDER_ITEM_STATUS_LABEL.DONE
  if (statuses.some((status) => status === 'CANCEL_REQUEST')) {
    return MALL_ORDER_ITEM_STATUS_LABEL.CANCEL_REQUEST
  }
  if (statuses.some((status) => status === 'RETURN_REQUEST')) {
    return MALL_ORDER_ITEM_STATUS_LABEL.RETURN_REQUEST
  }
  return MALL_ORDER_STATUS_LABEL.PAID
}

function buyerStatusClass(order: MallOrder) {
  if (order.status === 'PENDING' && order.payNotifiedAt) return 'is-confirming'
  if (order.status === 'PENDING') return 'is-pending'
  if (order.status === 'CANCELED') return 'is-canceled'
  const label = buyerStatusLabel(order)
  if (label === MALL_ORDER_ITEM_STATUS_LABEL.SHIPPING) return 'is-shipping'
  if (label === MALL_ORDER_ITEM_STATUS_LABEL.READY) return 'is-ready'
  if (label === MALL_ORDER_ITEM_STATUS_LABEL.DELIVERED) return 'is-delivered'
  if (label === MALL_ORDER_ITEM_STATUS_LABEL.DONE) return 'is-done'
  if (label === MALL_ORDER_ITEM_STATUS_LABEL.CANCEL_REQUEST) return 'is-claim'
  if (label === MALL_ORDER_ITEM_STATUS_LABEL.RETURN_REQUEST) return 'is-claim'
  return 'is-paid'
}

const filterTabs = computed(() => {
  const tabs: Array<{ key: FilterKey; label: string; count: number }> = [
    { key: 'ALL', label: '전체', count: orders.value.length },
    {
      key: 'PENDING',
      label: '결제대기',
      count: orders.value.filter((order) => matchesFilter(order, 'PENDING')).length,
    },
    {
      key: 'PAID',
      label: '결제완료',
      count: orders.value.filter((order) => matchesFilter(order, 'PAID')).length,
    },
    {
      key: 'READY',
      label: '발송준비',
      count: orders.value.filter((order) => matchesFilter(order, 'READY')).length,
    },
    {
      key: 'SHIPPING',
      label: '배송중',
      count: orders.value.filter((order) => matchesFilter(order, 'SHIPPING')).length,
    },
    {
      key: 'DELIVERED',
      label: '배송완료',
      count: orders.value.filter((order) => matchesFilter(order, 'DELIVERED')).length,
    },
    {
      key: 'CLAIM',
      label: '취소·반품',
      count: orders.value.filter((order) => matchesFilter(order, 'CLAIM')).length,
    },
  ]
  return tabs
})

const filteredOrders = computed(() =>
  orders.value.filter((order) => {
    if (!matchesFilter(order, filterKey.value)) return false
    if (!inRange(order.orderedAt)) return false
    const q = keyword.value.trim().toLowerCase()
    if (!q) return true
    const hay = `${order.code} ${order.itemSummary || ''} ${order.receiver || ''}`.toLowerCase()
    return hay.includes(q)
  }),
)

const STATUS_ORDER: MallOrderItemStatus[] = [
  'CANCEL_REQUEST',
  'RETURN_REQUEST',
  'PAID',
  'READY',
  'SHIPPING',
  'DELIVERED',
  'DONE',
  'WAITING',
  'CANCELED',
]

function shippingSummary(order: MallOrder) {
  const statuses = order.itemStatuses || []
  if (!statuses.length) return []

  const counts = statuses.reduce<Partial<Record<MallOrderItemStatus, number>>>((acc, status) => {
    acc[status] = (acc[status] || 0) + 1
    return acc
  }, {})

  return STATUS_ORDER.filter((key) => counts[key])
    .map((key) => ({
      key,
      label: MALL_ORDER_ITEM_STATUS_LABEL[key],
      count: counts[key] || 0,
      tone: `is-${key.toLowerCase()}`,
    }))
    .slice(0, 4)
}

function formatDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["mall-orders", { 'is-embedded': _unref(isEmbedded) }])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("header", _hoisted_2, [
        _cache[9] || (_cache[9] = _createElementVNode("div", null, [
          _createElementVNode("h1", null, "주문내역"),
          _createElementVNode("p", { class: "mall-orders__lead" }, "주문 상태와 배송을 확인합니다.")
        ], -1)),
        (unpaidPendingCount.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("p", _hoisted_4, [
                _createVNode(AppIcon, {
                  icon: "mdi-clock-outline",
                  size: 18
                }),
                _createTextVNode(" 결제를 기다리는 주문이 " + _toDisplayString(unpaidPendingCount.value) + "건 있습니다. ", 1),
                (oldestUnpaidPendingHours.value >= 24)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createTextVNode(" · 가장 오래된 건 " + _toDisplayString(Math.floor(oldestUnpaidPendingHours.value / 24)) + "일 경과 ", 1)
                    ], 64))
                  : (oldestUnpaidPendingHours.value >= 1)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createTextVNode(" · 가장 오래된 건 " + _toDisplayString(oldestUnpaidPendingHours.value) + "시간 경과 ", 1)
                      ], 64))
                    : _createCommentVNode("", true)
              ]),
              (firstUnpaidPending.value?.payMethod === 'TOSS')
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _createElementVNode("div", null, [
                      _createElementVNode("strong", null, _toDisplayString(_unref(formatPrice)(firstUnpaidPending.value.totalAmount)) + "원", 1),
                      _cache[4] || (_cache[4] = _createElementVNode("span", null, "카드·간편결제를 마쳐 주세요. 결제가 끝나면 발송이 시작됩니다.", -1))
                    ]),
                    _createElementVNode("div", _hoisted_6, [
                      _createVNode(_component_router_link, {
                        to: `/mall/orders/${firstUnpaidPending.value.code}`
                      }, {
                        default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                          _createTextVNode("결제하기", -1)
                        ]))]),
                        _: 1
                      }, 8, ["to"])
                    ])
                  ]))
                : (payment.value && firstUnpaidPending.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createElementVNode("div", null, [
                        _createElementVNode("strong", null, _toDisplayString(_unref(formatPrice)(firstUnpaidPending.value.totalAmount)) + "원", 1),
                        _createElementVNode("span", null, _toDisplayString(payment.value.bankName) + " " + _toDisplayString(payment.value.account) + " · 예금주 " + _toDisplayString(payment.value.holder), 1),
                        _createElementVNode("em", null, "입금자 " + _toDisplayString(firstUnpaidPending.value.depositor || firstUnpaidPending.value.receiver), 1)
                      ]),
                      _createElementVNode("div", _hoisted_8, [
                        _createElementVNode("button", {
                          type: "button",
                          onClick: _cache[0] || (_cache[0] = ($event: any) => (copyText(String(firstUnpaidPending.value.totalAmount), '입금 금액')))
                        }, " 금액 복사 "),
                        _createElementVNode("button", {
                          type: "button",
                          onClick: _cache[1] || (_cache[1] = ($event: any) => (copyText(payment.value.account.replace(/\s/g, ''), '계좌번호')))
                        }, " 계좌 복사 "),
                        _createVNode(_component_router_link, {
                          to: `/mall/orders/${firstUnpaidPending.value.code}`
                        }, {
                          default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                            _createTextVNode("입금 안내", -1)
                          ]))]),
                          _: 1
                        }, 8, ["to"])
                      ])
                    ]))
                  : _createCommentVNode("", true)
            ]))
          : (notifiedPendingCount.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _createElementVNode("p", _hoisted_10, [
                  _createVNode(AppIcon, {
                    icon: "mdi-check-circle-outline",
                    size: 18
                  }),
                  _createTextVNode(" 입금 확인을 기다리는 주문이 " + _toDisplayString(notifiedPendingCount.value) + "건 있습니다. ", 1)
                ]),
                (firstNotifiedPending.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                      _createElementVNode("div", null, [
                        _createElementVNode("strong", null, _toDisplayString(_unref(formatPrice)(firstNotifiedPending.value.totalAmount)) + "원", 1),
                        _cache[7] || (_cache[7] = _createElementVNode("span", null, "「입금했어요」 알림을 보냈습니다. 운영팀이 통장을 확인하면 배송이 시작됩니다.", -1)),
                        (firstNotifiedPending.value.depositor)
                          ? (_openBlock(), _createElementBlock("em", _hoisted_12, " 입금자 " + _toDisplayString(firstNotifiedPending.value.depositor), 1))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("div", _hoisted_13, [
                        _createVNode(_component_router_link, {
                          to: `/mall/orders/${firstNotifiedPending.value.code}`
                        }, {
                          default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                            _createTextVNode("주문 상세", -1)
                          ]))]),
                          _: 1
                        }, 8, ["to"])
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
      ]),
      _createVNode(MallMyFilterBar, {
        class: "mall-orders__filter",
        "from-date": _unref(fromDate),
        "to-date": _unref(toDate),
        keyword: keyword.value,
        preset: _unref(preset),
        placeholder: "주문번호·상품명",
        "onUpdate:fromDate": onFromDate,
        "onUpdate:toDate": onToDate,
        "onUpdate:keyword": _cache[2] || (_cache[2] = ($event: any) => (keyword.value = $event)),
        onPreset: onPreset,
        onSearch: reload
      }, null, 8, ["from-date", "to-date", "keyword", "preset"]),
      (!loading.value && orders.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filterTabs.value, (tab) => {
              return (_openBlock(), _createElementBlock("button", {
                key: tab.key,
                type: "button",
                role: "tab",
                class: _normalizeClass(["mall-orders__tab", { 'is-active': filterKey.value === tab.key }]),
                "aria-selected": filterKey.value === tab.key,
                onClick: ($event: any) => (filterKey.value = tab.key)
              }, [
                _createTextVNode(_toDisplayString(tab.label) + " ", 1),
                _createElementVNode("em", null, _toDisplayString(tab.count), 1)
              ], 10, _hoisted_15))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
            _createVNode(AppIcon, {
              icon: "mdi-loading",
              size: 40
            }),
            _cache[10] || (_cache[10] = _createElementVNode("p", null, "주문을 불러오는 중입니다…", -1))
          ]))
        : (!orders.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
              _createVNode(AppIcon, {
                icon: "mdi-clipboard-text-off-outline",
                size: 56
              }),
              _cache[12] || (_cache[12] = _createElementVNode("p", null, "주문내역이 없습니다.", -1)),
              _createVNode(_component_router_link, { to: "/mall" }, {
                default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                  _createTextVNode("쇼핑계속하기", -1)
                ]))]),
                _: 1
              })
            ]))
          : (!filteredOrders.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                _createVNode(AppIcon, {
                  icon: "mdi-filter-off-outline",
                  size: 48
                }),
                _cache[13] || (_cache[13] = _createElementVNode("p", null, "이 상태에 해당하는 주문이 없습니다.", -1)),
                _createElementVNode("button", {
                  type: "button",
                  class: "mall-orders__reset",
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (filterKey.value = 'ALL'))
                }, "전체 보기")
              ]))
            : (_openBlock(), _createElementBlock("ul", _hoisted_19, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredOrders.value, (order) => {
                  return (_openBlock(), _createElementBlock("li", {
                    key: order.code,
                    class: _normalizeClass(["mall-orders__card", {
            'is-unpaid': order.status === 'PENDING' && !order.payNotifiedAt,
            'is-confirming': order.status === 'PENDING' && Boolean(order.payNotifiedAt),
          }])
                  }, [
                    _createElementVNode("header", null, [
                      _createElementVNode("div", null, [
                        _createElementVNode("strong", null, _toDisplayString(order.code), 1),
                        _createElementVNode("time", null, _toDisplayString(formatDate(order.orderedAt)), 1)
                      ]),
                      _createElementVNode("span", {
                        class: _normalizeClass(["mall-orders__status", buyerStatusClass(order)])
                      }, _toDisplayString(buyerStatusLabel(order)), 3)
                    ]),
                    (order.status === 'PENDING')
                      ? (_openBlock(), _createElementBlock("p", {
                          key: 0,
                          class: _normalizeClass(["mall-orders__pending-note", { 'is-confirming': Boolean(order.payNotifiedAt) }])
                        }, [
                          _createVNode(AppIcon, {
                            icon: order.payNotifiedAt ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline',
                            size: 16
                          }, null, 8, ["icon"]),
                          (order.payMethod === 'TOSS')
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                _createTextVNode(_toDisplayString(pendingAgeLabel(order.orderedAt)) + " · 카드·간편결제를 마치지 않으면 자동 취소됩니다 ", 1)
                              ], 64))
                            : (order.payNotifiedAt)
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                  _createTextVNode(" 입금 알림을 보냈습니다. 운영팀 확인을 기다립니다. ")
                                ], 64))
                              : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                  _createTextVNode(_toDisplayString(pendingAgeLabel(order.orderedAt)) + " · 이체 후 「입금했어요」를 누르지 않으면 자동 취소됩니다 ", 1)
                                ], 64))
                        ], 2))
                      : _createCommentVNode("", true),
                    _createElementVNode("p", _hoisted_20, [
                      _createTextVNode(_toDisplayString(order.itemSummary || '주문 상품') + " ", 1),
                      (order.itemCount > 1)
                        ? (_openBlock(), _createElementBlock("em", _hoisted_21, "외 " + _toDisplayString(order.itemCount - 1) + "건", 1))
                        : _createCommentVNode("", true)
                    ]),
                    (order.status === 'PAID' && shippingSummary(order))
                      ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(shippingSummary(order), (badge) => {
                            return (_openBlock(), _createElementBlock("span", {
                              key: badge.key,
                              class: _normalizeClass(["mall-orders__ship-badge", badge.tone])
                            }, [
                              _createTextVNode(_toDisplayString(badge.label) + " ", 1),
                              (badge.count > 1)
                                ? (_openBlock(), _createElementBlock("em", _hoisted_23, _toDisplayString(badge.count), 1))
                                : _createCommentVNode("", true)
                            ], 2))
                          }), 128))
                        ]))
                      : _createCommentVNode("", true),
                    (trackItemsOf(order).length)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(trackItemsOf(order), (item) => {
                            return (_openBlock(), _createElementBlock("button", {
                              key: item.idx,
                              type: "button",
                              class: "mall-orders__track",
                              onClick: ($event: any) => (_unref(openTracking)({ ...item, orderCode: order.code }))
                            }, [
                              _createVNode(AppIcon, {
                                icon: "mdi-truck-outline",
                                size: 16
                              }),
                              _createElementVNode("span", null, _toDisplayString(item.courier || '택배') + " " + _toDisplayString(item.invoice), 1),
                              _cache[14] || (_cache[14] = _createTextVNode(" 배송조회 ", -1))
                            ], 8, _hoisted_25))
                          }), 128))
                        ]))
                      : _createCommentVNode("", true),
                    _createElementVNode("footer", null, [
                      _createElementVNode("div", null, [
                        _createElementVNode("span", null, _toDisplayString(order.receiver) + " · " + _toDisplayString(order.phone), 1),
                        _createElementVNode("p", null, _toDisplayString(order.address) + " " + _toDisplayString(order.addressDetail), 1)
                      ]),
                      _createElementVNode("div", _hoisted_26, [
                        _createElementVNode("strong", null, _toDisplayString(_unref(formatPrice)(order.totalAmount)) + "원", 1),
                        _createVNode(_component_router_link, {
                          to: `/mall/orders/${order.code}`,
                          class: "mall-orders__detail"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(order.status === 'PENDING' && order.payMethod === 'TOSS'
                    ? '결제하기'
                    : order.status === 'PENDING' && !order.payNotifiedAt
                      ? '입금 안내 보기'
                      : '주문 상세'), 1)
                          ]),
                          _: 2
                        }, 1032, ["to"])
                      ])
                    ])
                  ], 2))
                }), 128))
              ]))
    ]),
    (_unref(trackTarget))
      ? (_openBlock(), _createBlock(MallTrackingModal, {
          key: 0,
          "item-idx": _unref(trackTarget).idx,
          courier: _unref(trackTarget).courier,
          invoice: _unref(trackTarget).invoice,
          "product-name": _unref(trackTarget).name,
          "order-code": _unref(trackTarget).orderCode,
          onClose: onCloseTracking
        }, null, 8, ["item-idx", "courier", "invoice", "product-name", "order-code"]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})